import { reactive, computed, onMounted } from 'vue'
import type { ApocryphaBook, ApocryphaResult, ApocryphaSearchRequest } from '../types/apocrypha'
import { apiClient } from '../services/apiClient'

interface UseApocryphaSearchState {
  selectedBook: string | null
  results: ApocryphaResult[]
  totalCount: number
  isLoading: boolean
  error: string | null
  currentPage: number
  apocryphaBooks: ApocryphaBook[]
}

export function useApocryphaSearch() {
  const state = reactive<UseApocryphaSearchState>({
    selectedBook: 'all',
    results: [],
    totalCount: 0,
    isLoading: false,
    error: null,
    currentPage: 0,
    apocryphaBooks: [],
  })

  const hasResults = computed(() => state.results.length > 0)
  const isEmpty = computed(() => !state.isLoading && !state.error && state.results.length === 0)

  /**
   * 載入次經書卷清單
   */
  const loadApocryphaBooks = async (): Promise<void> => {
    state.isLoading = true
    state.error = null
    try {
      const response = await apiClient.getApocryphaBooks()

      if (!response.success) {
        throw new Error(response.error || '無法載入次經書卷清單')
      }

      state.apocryphaBooks = response.data || []
      if (state.apocryphaBooks.length === 0) {
        state.error = '目前沒有可用的次經書卷'
      }
    } catch (error) {
      console.error('Load apocrypha books error:', error)
      state.error = error instanceof Error ? error.message : '發生未知錯誤'
    } finally {
      state.isLoading = false
    }
  }

  /**
   * 選擇次經書卷
   */
  const selectBook = (bookId: string | null): void => {
    state.selectedBook = bookId || 'all'
    state.results = []
    state.totalCount = 0
    state.error = null
    state.currentPage = 0
  }

  /**
   * 搜尋次經內容
   */
  const search = async (query: string, bookId?: string, page: number = 0): Promise<void> => {
    if (!query) {
      state.error = '請輸入搜尋關鍵字'
      return
    }

    state.isLoading = true
    state.error = null
    state.currentPage = page
    const activeBookId = bookId || state.selectedBook || 'all'

    try {
      const request: ApocryphaSearchRequest = {
        query,
        bookId: activeBookId === 'all' ? undefined : activeBookId,
        page,
        limit: 500,
      }

      const response = await apiClient.searchApocrypha(request)

      if (!response.success || !response.data) {
        throw new Error(response.error || '搜尋失敗')
      }

      state.results = response.data.results
      state.totalCount = response.data.total
      state.currentPage = response.data.page
    } catch (error) {
      state.error = error instanceof Error ? error.message : '發生未知錯誤'
      state.results = []
      state.totalCount = 0
    } finally {
      state.isLoading = false
    }
  }

  /**
   * 清除搜尋結果
   */
  const clearResults = (): void => {
    state.results = []
    state.totalCount = 0
    state.error = null
    state.currentPage = 0
  }

  onMounted(async () => {
    await loadApocryphaBooks()
  })

  return {
    // State
    state,
    selectedBook: computed(() => state.selectedBook),
    results: computed(() => state.results),
    totalCount: computed(() => state.totalCount),
    isLoading: computed(() => state.isLoading),
    error: computed(() => state.error),
    currentPage: computed(() => state.currentPage),
    apocryphaBooks: computed(() => state.apocryphaBooks),
    // Computed
    hasResults,
    isEmpty,
    // Methods
    loadApocryphaBooks,
    selectBook,
    search,
    clearResults,
  }
}
